import { useState } from "react";
import { useAppDispatch, useAppSelector } from "../../../Redux/hooks";
import {
  handleTabErrors,
  setQuestionModalDetails,
  setQuestionValidation,
} from "../../../Redux/reducers/questionSlice";
import {
  QuestionLabelMap,
  choice_types,
  no_advanced_types,
  no_validation_types,
} from "../questionTypes";
import {
  Box,
  Button,
  CircularProgress,
  Divider,
  Modal,
  SxProps,
  Tab,
  Tabs,
  Typography,
} from "@mui/material";
import { a11yProps, ModalTabPanel } from "../../Common/UI/TabPanel";
import {
  ModalDrawerButtonStyles,
  ModalDrawerStyles,
  ModalHeaderTitleStyles,
} from "../../Common/styles/modal";
import ChoiceTab from "./ChoiceTab";
import { BasicTab } from "./BasicTab";
import { saveQuestion } from "../../../Redux/actions/questionAction";
import { submitValidation } from "./utils";
import RepeatedConfigTab from "./RepeatedConfigTab";
import ValidationTab from "./ValidationTab";
import DependencyTab from "./DependencyTab";
import GridConfigTab from "./GridConfigTab";
import { AdvancedTab } from "./AdvancedTab";
import { TemplateTab } from "./TemplateTab";
import LogicJumpsTab from "./LogicJumpsTab";
import { AddressTab } from "./AddressTab";

export enum ValidationTypes {
  error = "error",
  warning = "warning",
  info = "info",
}

const QuestionPropertiesModal: React.FC = () => {
  const dispatch = useAppDispatch();

  const isLoading = useAppSelector(({ question }) => question.loading);
  const editable = useAppSelector(({ question }) => question.editable);
  const modalIsEdit = useAppSelector(({ question }) => question.modalIsEdit);
  const displayLogic = useAppSelector((state) => state.study.displayLogic);

  const currentQuestion = useAppSelector(
    ({ question }) => question.modalQuestion
  );
  const isBasicError = useAppSelector(({ question }) => question.isBasicError);
  const isTemplateError = useAppSelector(
    ({ question }) => question.isTemplateError
  );
  const isAddressError = useAppSelector(
    ({ question }) => question.isAddressError
  );
  const isChoicesError = useAppSelector(
    ({ question }) => question.isChoicesError
  );
  const isRepeatedConfigError = useAppSelector(
    ({ question }) => question.isRepeatedConfigError
  );
  const isGridConfigError = useAppSelector(
    ({ question }) => question.isGridConfigError
  );
  const isValidationsError = useAppSelector(
    ({ question }) => question.isValidationsError
  );
  const isDependencyError = useAppSelector(
    ({ question }) => question.isDependencyError
  );
  const isLogicJumpsError = useAppSelector(
    ({ question }) => question.isLogicJumpsError
  );

  const formLevel = useAppSelector(({ question }) => question.formLevel);

  const computedDisplayLogic =
    formLevel === "platform" ? "dependencies" : displayLogic;

  const [tabIndex, setTabIndex] = useState("basic");
  // const [noLocalTabError, setNoLocalTabError] = useState(true);

  let indicatorColor = "primary.main";
  if (tabIndex === "basic" && isBasicError) {
    indicatorColor = "error.main";
  } else if (tabIndex === "choices" && isChoicesError) {
    indicatorColor = "error.main";
  } else if (tabIndex === "validations" && isValidationsError) {
    indicatorColor = "error.main";
  } else if (tabIndex === "dependency" && isDependencyError) {
    // } else if (tabIndex === "dependency" && isDependencyError && !noLocalTabError ) {
    indicatorColor = "error.main";
  } else if (tabIndex === "logicJumps" && isLogicJumpsError) {
    indicatorColor = "error.main";
  } else if (tabIndex === "template" && isTemplateError) {
    indicatorColor = "error.main";
  }

  const closeModal = () => {
    dispatch(
      setQuestionModalDetails({
        show: false,
        question: null,
        modalIsEdit: false,
        questionValidations: {},
      })
    );
    dispatch(
      handleTabErrors({
        errorHandler: {
          basic: "",
          choices: "",
          repeatedConfig: "",
          gridConfig: "",
          validations: "",
          dependency: "",
          logicJumps: "",
          template: "",
          address: "",
        },
      })
    );
  };

  const submitHandler = () => {
    const { errorHandler, questionValidations } =
      submitValidation(currentQuestion);
    dispatch(handleTabErrors({ errorHandler: errorHandler }));
    dispatch(setQuestionValidation(questionValidations));

    if (errorHandler.ok && !questionValidations.varname) {
      dispatch(saveQuestion(modalIsEdit));
    }
  };

  const changetabIndex = (_: any, oldValue: string) => {
    setTabIndex(oldValue);
  };

  const getTabSx = (isError: boolean): SxProps => ({
    textTransform: "capitalize",
    color: isError ? "error.main" : "primary.main",
    "&.Mui-selected": {
      color: isError ? "error.main" : "primary.main",
    },
  });

  return (
    <Modal open={true}>
      <Box sx={ModalDrawerStyles}>
        <Typography sx={ModalHeaderTitleStyles} mb={1}>
          {currentQuestion
            ? QuestionLabelMap[currentQuestion.type]
            : "Question"}
        </Typography>
        <Box sx={{ borderBottom: 1, borderBottomColor: "#E5E7EB" }}>
          <Tabs
            value={tabIndex}
            onChange={changetabIndex}
            aria-label="Modal Settings"
            sx={{
              "& .MuiTabs-indicator": {
                backgroundColor: indicatorColor,
              },
            }}
          >
            <Tab
              label="Basic"
              value="basic"
              {...a11yProps(0)}
              sx={getTabSx(!!isBasicError)}
            />
            {currentQuestion.type === "calculated_field" && (
              <Tab
                label="Calculation Config"
                value="template"
                {...a11yProps(1)}
                sx={getTabSx(!!isTemplateError)}
              />
            )}
            {choice_types.includes(currentQuestion.type) && (
              <Tab
                label="Choices"
                value="choices"
                {...a11yProps(2)}
                sx={getTabSx(!!isChoicesError)}
              />
            )}
            {currentQuestion.type === "repeated_data" && (
              <Tab
                label="Repeated Config"
                value="repeatedConfig"
                {...a11yProps(3)}
                sx={getTabSx(!!isRepeatedConfigError)}
              />
            )}
            {currentQuestion.type === "grid" && (
              <Tab
                label="Grid Config"
                value="gridConfig"
                {...a11yProps(4)}
                sx={getTabSx(!!isGridConfigError)}
              />
            )}
            {currentQuestion.type === "address" && (
              <Tab
                label="Address Config"
                value="addressConfig"
                {...a11yProps(5)}
                sx={getTabSx(!!isAddressError)}
              />
            )}
            {!no_validation_types.includes(currentQuestion.type) && (
              <Tab
                label="Validation"
                value="validations"
                {...a11yProps(6)}
                sx={getTabSx(!!isValidationsError)}
              />
            )}

            {computedDisplayLogic === "dependencies" && (
              <Tab
                label="Dependencies"
                value="dependency"
                {...a11yProps(7)}
                sx={getTabSx(!!isDependencyError)}
                // sx={getTabSx(!!isDependencyError || !noLocalTabError)}
              />
            )}
            {!no_validation_types.includes(currentQuestion.type) &&
              computedDisplayLogic === "logic_jumps" && (
                <Tab
                  label="Logic Jumps"
                  value="logicJumps"
                  {...a11yProps(8)}
                  sx={getTabSx(!!isLogicJumpsError)}
                />
              )}
            {!no_advanced_types.includes(currentQuestion.type) && (
              <Tab
                label="Advanced"
                value="advanced"
                {...a11yProps(9)}
                sx={getTabSx(false)}
              />
            )}
          </Tabs>
        </Box>
        <ModalTabPanel value={tabIndex} index={"basic"}>
          <BasicTab isEdit={modalIsEdit} question={currentQuestion} />
        </ModalTabPanel>
        <ModalTabPanel value={tabIndex} index={"template"}>
          <TemplateTab question={currentQuestion} />
        </ModalTabPanel>
        <ModalTabPanel value={tabIndex} index={"choices"}>
          <ChoiceTab question={currentQuestion} />
        </ModalTabPanel>
        <ModalTabPanel value={tabIndex} index={"repeatedConfig"}>
          <RepeatedConfigTab question={currentQuestion} />
        </ModalTabPanel>
        <ModalTabPanel value={tabIndex} index={"gridConfig"}>
          <GridConfigTab question={currentQuestion} />
        </ModalTabPanel>
        <ModalTabPanel value={tabIndex} index={"addressConfig"}>
          <AddressTab question={currentQuestion} />
        </ModalTabPanel>
        <ModalTabPanel value={tabIndex} index={"validations"}>
          <ValidationTab question={currentQuestion} />
        </ModalTabPanel>
        <ModalTabPanel value={tabIndex} index={"dependency"}>
          <DependencyTab
            question={currentQuestion}
            // setNoLocalTabError={setNoLocalTabError}
          />
        </ModalTabPanel>
        <ModalTabPanel value={tabIndex} index={"logicJumps"}>
          <LogicJumpsTab question={currentQuestion} />
        </ModalTabPanel>
        <ModalTabPanel value={tabIndex} index={"advanced"}>
          <AdvancedTab question={currentQuestion} />
        </ModalTabPanel>

        <Box sx={{ mt: "auto" }}>
          <Divider />
          <Box sx={ModalDrawerButtonStyles}>
            <Button
              variant="outlined"
              onClick={closeModal}
              disabled={isLoading}
            >
              {editable ? "Cancel" : "Close"}
            </Button>
            {editable && (
              <Button
                variant="contained"
                onClick={submitHandler}
                // sx={{ ml: 2 }}
                disabled={isLoading}
                // disabled={isLoading || !noLocalTabError}
              >
                {isLoading ? (
                  <CircularProgress size={24} />
                ) : modalIsEdit ? (
                  "Save"
                ) : (
                  "Add"
                )}
              </Button>
            )}
          </Box>
        </Box>
      </Box>
    </Modal>
  );
};

export default QuestionPropertiesModal;
